import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import DishForm from '../components/DishForm';
import ConfirmModal from '../components/ConfirmModal';
import { useTranslation } from '../contexts/LanguageContext';
import { 
  getMenus, 
  getCategoriesByMenu, 
  getDishesByMenu, 
  createDish, 
  updateDish, 
  deleteDish,
  getSettings
} from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { formatPrice } from '../utils/currencyUtils';
import './Dishes.css';

const Dishes = () => {
  const { t } = useTranslation();
  const { categoryId } = useParams();
  const [dishes, setDishes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menus, setMenus] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categoryId || '');
  const [settings, setSettings] = useState({
    currency: 'LBP',
    dollarRate: 89000
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDish, setCurrentDish] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [dishToDelete, setDishToDelete] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMenus = async () => {
    try {
      const response = await getMenus();
      setMenus(response.data);
      // Don't auto-select anymore - let user choose
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch menus' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async (menuId) => {
    try {
      const response = await getCategoriesByMenu(menuId);
      setCategories(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedCategory(response.data[0]._id);
      } else {
        setDishes([]);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch categories' });
    }
  };

  const fetchDishes = async (menuId) => {
    try {
      const response = await getDishesByMenu(menuId);
      setDishes(response.data || []);
    } catch (error) {
      console.error('Error fetching dishes:', error);
      setMessage({ type: 'error', text: 'Failed to fetch dishes' });
    }
  };

  useEffect(() => {
    fetchMenus();
    fetchSettings();
  }, []);

  useEffect(() => {
    if (selectedMenu) {
      fetchCategories(selectedMenu);
      fetchDishes(selectedMenu);
    }
  }, [selectedMenu]);

  const fetchSettings = async () => {
    try {
      const response = await getSettings();
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleMenuChange = (e) => {
    const menuId = e.target.value;
    setSelectedMenu(menuId);
    setSelectedCategory(''); // Reset category when menu changes
  };

  const handleAddDish = () => {
    setCurrentDish(null);
    setIsModalOpen(true);
  };

  const handleEditDish = (dish) => {
    setCurrentDish(dish);
    setIsModalOpen(true);
  };

  const handleDeleteDish = (dish) => {
    setDishToDelete(dish);
    setIsConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    if (dishToDelete) {
      try {
        await deleteDish(dishToDelete._id);
        setMessage({ type: 'success', text: t('dishDeleted') });
        fetchDishes(selectedMenu); // Fetch dishes for the current menu
      } catch (error) {
        console.error('Error deleting dish:', error);
        setMessage({ type: 'error', text: t('failedToDeleteDish') });
      } finally {
        setIsConfirmModalOpen(false);
        setDishToDelete(null);
      }
    }
  };

  const handleDishFormSubmit = async (dishData, isEditing) => {
    try {
      if (isEditing && currentDish) {
        await updateDish(currentDish._id, dishData);
        setMessage({ type: 'success', text: t('dishUpdated') });
      } else {
        await createDish(dishData);
        setMessage({ type: 'success', text: t('dishAdded') });
      }
      fetchDishes(selectedMenu); // Refresh dishes list
      setIsModalOpen(false);
      setCurrentDish(null);
    } catch (error) {
      setMessage({ type: 'error', text: isEditing ? t('failedToUpdateDish') : t('failedToSaveDish') });
    }
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('dishes')}</h1>
        <button onClick={handleAddDish} className="btn btn-primary add-dish-top-btn">
          ➕ {t('addNewDish')}
        </button>
      </div>

      {message && (
        <div className={`message message-${message.type}`}>
          {message.type === 'success' ? '✓' : '✗'} {message.text}
        </div>
      )}

      {/* Menu Selector */}
      <div className="menu-selector-section" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          {t('selectMenu')}:
        </label>
        <select
          value={selectedMenu}
          onChange={handleMenuChange}
          className="form-select"
          style={{ width: '100%', maxWidth: '400px' }}
        >
          <option value="">{t('chooseMenu') || 'Choose a menu'}</option>
          {menus.map(menu => (
            <option key={menu._id} value={menu._id}>{menu.name}</option>
          ))}
        </select>
      </div>

      {selectedMenu && categories.length > 0 && (
        <div className="categories-section">
          <h2 className="section-title">{t('dishesFor')} {menus.find(m => m._id === selectedMenu)?.name}</h2>
          <div className="categories-section">
            {categories.map((category) => (
              <div key={category._id} className="category-section">
                <div className="category-header">
                  <h3 className="category-title">{category.name}</h3>
                  <div className="category-stats">
                    <div className="stats-item">
                      <div className="stats-icon">🍽️</div>
                      <span>{t('dishesCount')}</span>
                    </div>
                    <div className="stats-item">
                      <div className="stats-icon">💰</div>
                      <span>{t('imagesCount')}</span>
                    </div>
                  </div>
                </div>
                
                <div className="category-dishes">
                  {(() => {
                    const categoryDishes = dishes.filter(dish => dish.categoryId === category._id);
                    console.log(`Category ${category.name} dishes:`, categoryDishes);
                    return categoryDishes.length > 0;
                  })() ? (
                    <table className="dishes-table">
                      <thead>
                        <tr>
                          <th>{t('image')}</th>
                          <th>{t('name')}</th>
                          <th>{t('category')}</th>
                          <th>{t('price')}</th>
                          <th>{t('status')}</th>
                          <th>{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dishes.filter(dish => dish.categoryId === category._id).map((dish) => (
                          <tr key={dish._id} className="dish-row">
                            <td className="dish-image-cell">
                              <div className="dish-image-table">
                                {getImageUrl(dish.imageUrl) ? (
                                  <img src={getImageUrl(dish.imageUrl)} alt={dish.name} />
                                ) : (
                                  <div className="no-image-table">🍽️</div>
                                )}
                              </div>
                            </td>
                            <td className="dish-name-cell">
                              <span className="dish-name-table">{dish.name}</span>
                            </td>
                            <td className="dish-category-cell">
                              <span className="dish-category-table">{category.name}</span>
                            </td>
                            <td className="dish-price-cell">
                              <span className="dish-price-table">{formatPrice(dish.price, settings?.currency || 'LBP', settings?.dollarRate || 89000)}</span>
                            </td>
                            <td className="dish-status-cell">
                              <span className="status-badge active">
                                <span className="status-icon">✓</span>
                                {t('active')}
                              </span>
                            </td>
                            <td className="dish-actions-cell">
                              <div className="action-buttons">
                                <button 
                                  className="btn-action btn-edit-table"
                                  onClick={() => handleEditDish(dish)}
                                  title={t('edit')}
                                >
                                  ✏️
                                </button>
                                <button 
                                  className="btn-action btn-delete-table"
                                  onClick={() => handleDeleteDish(dish)}
                                  title={t('delete')}
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="empty-category">
                      <div className="empty-category-icon">📭</div>
                      <p>{t('noDishesInCategory')}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!selectedMenu && (
        <div className="empty-state" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📋</div>
          <p>{t('pleaseSelectMenuFirst') || 'Please select a menu from the dropdown above to view its dishes'}</p>
        </div>
      )}

      {selectedMenu && categories.length === 0 && (
        <div className="empty-state" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📂</div>
          <p>{t('noCategoriesInMenu') || 'This menu has no categories. Please add categories first.'}</p>
        </div>
      )}

      {isModalOpen && (
        <DishForm
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleDishFormSubmit}
          dish={currentDish}
          menus={menus}
          availableCategories={categories}
          selectedCategory={selectedCategory}
        />
      )}

      {isConfirmModalOpen && (
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          onCancel={() => setIsConfirmModalOpen(false)}
          onConfirm={confirmDelete}
          message={`${t('confirm')} "${dishToDelete?.name}"?`}
        />
      )}
    </div>
  );
};

export default Dishes;
